class Wishlist {
  constructor() {
    // --- flags & storage ---
    this.pageTemplate = 'page.wishlist';
    this.storageKey = 'm-wishlist-products';
    // Make sure MinimogSettings is defined before accessing properties
    this.isWishlistPage = typeof MinimogSettings !== 'undefined' && MinimogSettings.template === this.pageTemplate;

    // --- state ---
    this.products = this.loadFromStorage();
    this.productNodes = {};

    // --- classes/selectors used by the theme ---
    this.addedClass = 'added-to-wishlist';
    this.hasItemClass = 'wishlist-has-item';
    this.selectors = {
      container: '.m-wishlist-page-content__wrapper',
      noProducts: '.m-wishlist-no-products',
      wrapper: '.m-wishlist-card',
      productCard: '.m-product-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-remove-button',
      count: '.m-wishlist-count',
    };

    this.init();
  }

  loadFromStorage() {
      try {
          const stored = localStorage.getItem(this.storageKey);
          return stored ? Array.from(new Set(JSON.parse(stored))) : [];
      } catch (e) {
          console.error('Error loading wishlist from storage:', e);
          return [];
      }
  }


  // ---------------- lifecycle ----------------
  init = async () => {
    if (this.isWishlistPage) {
      await this.renderWishlistPage();
      // Since buttons are added dynamically, we delegate the event instead of binding to static elements
      this.addEventToRemoveButtons();
    }
    this.setWishlistButtonsState();
    this.addEventToWishlistButtons();
    this.updateWishlistCount();
  };

  // ---------------- storage helpers ----------------
  saveToStorage = () => {
    this.products = Array.from(new Set(this.products));
    localStorage.setItem(this.storageKey, JSON.stringify(this.products));
  };

  addToWishlist(handle) {
    if (handle && !this.products.includes(handle)) {
      this.products.push(handle);
      this.saveToStorage();
    }
  }

  removeFromWishlist(handle) {
    this.products = this.products.filter(h => h !== handle);
    this.saveToStorage();
  }

  // ---------------- UI state ----------------
  setWishlistButtonsState = () => {
    document.querySelectorAll(this.selectors.wishlistButton).forEach(btn => {
      const handle = btn && btn.dataset.productHandle;
      if (!handle) return;
      const inList = this.products.includes(handle);

      if (inList && !btn.classList.contains(this.addedClass)) {
        this.toggleButtonState(btn, true);
        
        // On wishlist page, button behaves as remove
        if (this.isWishlistPage) {
          btn.classList.remove(this.selectors.wishlistButton.substring(1)); // remove '.'
          btn.classList.add(this.selectors.removeButton.substring(1)); // remove '.'
        }
      } else if (!inList && btn.classList.contains(this.addedClass)) {
          // If not in list, ensure button is not active (sync across tabs/windows potentially)
           this.toggleButtonState(btn, false);
      }
    });
  };

  updateWishlistCount = () => {
    const count = this.products.length;

    [...document.querySelectorAll(this.selectors.count)].forEach(node => {
      node.textContent = count;
      node.classList.toggle('m:hidden', count < 1);
    });

    document.body.classList[count ? 'add' : 'remove'](this.hasItemClass);
  };

  // ---------------- events ----------------
  addEventToWishlistButtons = () => {
    // Helper handles delegation
    if (typeof addEventDelegate === 'function') {
        addEventDelegate({
          selector: this.selectors.wishlistButton,
          handler: (ev, btn) => {
            ev.preventDefault();
            const handle = btn && btn.dataset.productHandle;
            if (!handle) return;

            const shouldAdd = !btn.classList.contains(this.addedClass);
            this.toggleButtonState(btn, shouldAdd);
            this.updateWishlistCount();

            // Sync other buttons for same product
            document.querySelectorAll(`${this.selectors.wishlistButton}[data-product-handle="${handle}"]`).forEach(other => {
              if (other && other !== btn) {
                 // Determine state based on whether we just added or removed
                 const currentState = other.classList.contains(this.addedClass);
                 // If we added, other button should be added. If we removed, other should be removed.
                 if (shouldAdd !== currentState) {
                    this.toggleButtonState(other, shouldAdd);
                 }
              }
            });
          }
        });
    } else {
        console.warn('addEventDelegate function is missing. Wishlist buttons may not work correctly.');
    }
  };

  toggleButtonState = (btn, add) => {
    const handle = btn && btn.dataset.productHandle;
    const textEl = btn && btn.querySelector(this.selectors.wishlistText);

    if (add) {
      if(handle) this.addToWishlist(handle);
      btn.classList.add(this.addedClass);
    } else {
      if(handle) this.removeFromWishlist(handle);
      btn.classList.remove(this.addedClass);
    }

    // Toggle text if revert text is available
    if (textEl && textEl.dataset.revertText) {
      const tmp = textEl.dataset.revertText;
      textEl.dataset.revertText = textEl.textContent;
      textEl.textContent = tmp;
    }
  };

  addEventToRemoveButtons = () => {
    if (typeof addEventDelegate === 'function') {
        addEventDelegate({
        selector: this.selectors.removeButton,
        handler: (ev, btn) => {
            ev.preventDefault();

            // Find wrapper card
            const card = btn && btn.closest(this.selectors.wrapper);
            const handle = btn && btn.dataset.productHandle;

            if (!card || !handle) return;

            // Simple fade out animation
            card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            card.style.opacity = '0';
            card.style.transform = 'scale(0.9)';

            setTimeout(() => {
                card.remove();
                this.removeFromWishlist(handle);
                this.updateWishlistCount();
                if (!this.products.length) this.showNoProductsMessage();
            }, 300);
        }
        });
    }
  };

  // Helper to create the remove button for the card (mobile style usually, but applied generally in grid)
  createRemoveButton(handle) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'm-wishlist-remove-button m:block md:m:hidden'; // Matches legacy classes mostly
    // Add custom class for styling if needed, or rely on existing structure
    el.setAttribute('data-product-handle', handle);
    el.setAttribute('aria-label', 'Remove from Wishlist');
    el.innerHTML = '<svg class="m-svg-icon--medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';
    return el;
  }

  // ---------------- render page ----------------
  renderWishlistPage = async () => {
    const container = document.querySelector(this.selectors.container);
    if (!container) return;

    // Clear container initially (or handle duplicates if implementing load more)
    container.innerHTML = '';

    if (this.products.length === 0) {
      this.showNoProductsMessage();
      container.classList.add('is-visible');
      return;
    }

    // Loading state
    container.classList.add('m-wishlist-loading'); // Theme should style this or ignored
    
    // Batch fetch products using search API
    // We construct a query like "handle:prod1 OR handle:prod2 ..."
    // Shopify Search API url limit is ~2k chars usually, harmless to split if very large
    const chunkSize = 20; // manageable chunk size
    const chunkedHandles = [];
    for (let i = 0; i < this.products.length; i += chunkSize) {
        chunkedHandles.push(this.products.slice(i, i + chunkSize));
    }

    try {
        const fetchPromises = chunkedHandles.map(async (handles) => {
            const query = handles.map(h => `handle:${h}`).join(' OR ');
            // Ensure we only get products, view=wishlist-cards renders our minimal snippet
            const searchUrl = `/search?type=product&q=${encodeURIComponent(query)}&view=wishlist-cards`;
            
            const response = await fetch(searchUrl);
            if (!response.ok) throw new Error('Network response was not ok');
            const text = await response.text();
            
            // Parse HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/html');
            return Array.from(doc.querySelectorAll(this.selectors.wrapper));
        });

        const results = await Promise.all(fetchPromises);
        const allCards = results.flat();

        // Sort cards to match the order in this.products (optional but nice)
        // The search results might not be in the requested order
        const cardsMap = {};
        allCards.forEach(card => {
            const handle = card.dataset.productHandle;
            if (handle) cardsMap[handle] = card;
        });

        let hasItems = false;
        this.products.forEach(handle => {
            const card = cardsMap[handle];
            if (card) {
                // Determine if we need to add the remove button manually 
                // (The template might not include it if it's a generic product card)
                // Existing code added it dynamically.
                if (!card.querySelector(this.selectors.removeButton)) {
                     card.appendChild(this.createRemoveButton(handle));
                }
                
                // Ensure visibility classes
                card.classList.remove('m:hidden');
                
                // Add to container
                container.appendChild(card);
                hasItems = true;
            }
        });

        if (!hasItems) {
             this.showNoProductsMessage();
        } else {
             // Initialize theme specific logic (color swatches, comparison, etc) if available
             if (window.MinimogTheme && MinimogTheme.CompareProduct) {
                 MinimogTheme.CompareProduct.setCompareButtonsState();
             }
             // Trigger any lazy loading or currency scripts if needed
             // window.dispatchEvent(new Event('resize')); 
        }

    } catch (error) {
        console.error('Error fetching wishlist products:', error);
        // Fallback or error message could go here
    } finally {
        container.classList.remove('m-wishlist-loading');
        container.classList.add('is-visible');
    }
  };

  showNoProductsMessage = () => {
    const container = document.querySelector(this.selectors.container);
    const emptyBox = document.querySelector(this.selectors.noProducts);
    
    if (container) container.classList.add('m:hidden');
    if (emptyBox) emptyBox.classList.remove('m:hidden');
  };
}

// Initializing the instance
if (typeof MinimogTheme !== 'undefined') {
    MinimogTheme.Wishlist = new Wishlist();
} else {
    // Fallback if MinimogTheme isn't ready yet (rare)
    window.addEventListener('DOMContentLoaded', () => {
         if (typeof MinimogTheme !== 'undefined') {
            MinimogTheme.Wishlist = new Wishlist();
         }
    });
}
